import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import { TbLeaf } from 'react-icons/tb';
import { FiCalendar, FiEdit3 } from 'react-icons/fi';
import ReservationDepositSection from './ReservationDepositSection';
import ReactQuill from 'react-quill';
import Lottie from 'react-lottie';
import { formatLongDate } from '../WidgetConfig';
import watermark from '../../../Assets/images/water.png';
import { LuClock4, LuUsers, LuWine } from 'react-icons/lu';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';
import Loader from '../../../Main/Components/Common/Loader';

const SummaryRow = ({ icon, value, borderBottom = true }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      py: 1.5,
      borderBottom: borderBottom ? '1px solid' : 'none',
      borderColor: 'divider',
    }}
  >
    <Box sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography
        sx={{ fontSize: '14px', fontWeight: 700, color: 'text.primary' }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

const ReservationConfirmPanel = ({
  floor,
  restaurant,
  restaurantName,
  date,
  time,
  guest,
  guestDetails,
  floorPlanName,
  Dietary,
  specialRequest,
  occasion,
  otherOccasions,
  deposit,
  deposit_charge,
  waitlist,
  currentColor,
  IS_SHIRO,
  IS_HARDROCK,
  loading,
  rulesChecked,
  handleOpen,
  pointValue,
  point_id,
  lottieOptions,
  onEditDetails,
  onConfirm,
  isConfirmDisabled,
  confirmLabel,
  confirmLoading,
}) => {
  const dietaryLabel =
    Array.isArray(Dietary) && Dietary.length > 0 ? Dietary.join(', ') : 'N/A';
  const occasionLabel = occasion || otherOccasions || 'N/A';
  const additionalCount = guestDetails?.length || 0;
  const totalGuests = (parseFloat(guest) || 0) + additionalCount;
  const hasDeposit = Number(deposit) > 0;
  const isWaitlist = waitlist === 'true';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const IS_TAPAYANKI = floor === '51ccccb7-d8e9-4132-b317-4d253c095263';

  return (
    <>
      {confirmLoading && <Loader />}
      <Grid container spacing={{ xs: 3, md: 4 }} alignItems="flex-start">
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '18px',
              overflow: 'hidden',
              boxShadow: isDark ? 'none' : '0 18px 45px rgba(16, 24, 40, 0.08)',
              pb: 4,
            }}
          >
            <Box
              sx={{
                bgcolor: 'primary.main',
                backgroundImage: `url(${watermark})`,
                backgroundSize: '200px',
                backgroundPosition: 'center',
                backgroundRepeat: 'repeat',
                px: 3,
                py: 3,
              }}
            >
              <h1
                className="text-2xl font-bold optima my-0"
                style={{ color: theme.palette.primary.contrastText }}
              >
                Reservation Summary
              </h1>
              <Typography
                sx={{
                  color: isDark ? 'text.secondary' : '#FFE4E6',
                  fontSize: '13px',
                  fontWeight: 700,
                  mt: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {restaurant?.RestaurantName || restaurantName}
              </Typography>
            </Box>
            <div className="px-4">
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: 'text.primary',
                  mt: 2,
                  mb: 1,
                }}
              >
                Booking Details
              </Typography>
              <Box
                sx={{
                  px: 2.5,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '14px',
                }}
              >
                <SummaryRow
                  icon={<FiCalendar />}
                  label="Date"
                  value={formatLongDate(date)}
                />
                <SummaryRow
                  icon={<LuClock4 />}
                  label="Time"
                  value={time || '--'}
                />
                <SummaryRow
                  icon={<LuUsers />}
                  label="Guests"
                  value={`${totalGuests} guest${totalGuests === 1 ? '' : 's'}`}
                />
                <SummaryRow
                  icon={<LuWine />}
                  label="Area"
                  value={floorPlanName || '--'}
                  borderBottom={false}
                />
              </Box>
            </div>

            <Box sx={{ px: 2.5, mt: 1.5 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Dietary Information
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2.5,
                  py: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '14px',
                }}
              >
                <TbLeaf style={{ color: theme.palette.text.secondary }} />
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  Dietary Restrictions: {dietaryLabel}
                </Typography>
                {occasionLabel !== 'N/A' && (
                  <Typography
                    sx={{ fontSize: '13px', color: 'text.secondary', mt: 0.5 }}
                  >
                    Occasion: {occasionLabel}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ px: 2.5, mt: 1.5 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Special Requests
              </Typography>

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '14px',
                  px: 2.5,
                  py: 2,
                }}
              >
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  {specialRequest?.trim() || 'N/A'}
                </Typography>
              </Box>
            </Box>

            {hasDeposit && (
              <>
                <Divider sx={{ my: 3, mx: 3 }} />
                <Box sx={{ px: 2.5, pb: 2.5 }}>
                  <ReservationDepositSection
                    deposit={deposit}
                    deposit_charge={deposit_charge}
                    restaurantName={restaurantName}
                    guest={guest}
                    currentColor={currentColor}
                    IS_SHIRO={IS_SHIRO}
                    IS_HARDROCK={IS_HARDROCK}
                    IS_TAPAYANKI={IS_TAPAYANKI}
                  />
                </Box>
              </>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            sx={{
              position: { xs: 'static', md: 'sticky' },
              top: { md: 96 },
              alignSelf: 'flex-start',
            }}
          >
            <Box
              sx={{
                bgcolor: isDark ? 'background.paper' : '#FFFBEB',
                border: '1px solid',
                borderColor: isDark ? 'divider' : '#FEE685',
                borderRadius: '14px',
                p: 2,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '14px',
                  color: isDark ? 'text.primary' : '#973C00',
                  mb: 1,
                }}
              >
                Cancellation Policy
              </Typography>
              {loading ? (
                <>
                  <Skeleton variant="text" animation="wave" />
                  <Skeleton width={100} variant="text" animation="wave" />
                  <Skeleton variant="text" animation="wave" />
                  <Skeleton variant="text" width={'80%'} animation="wave" />
                </>
              ) : restaurant?.Experience?.CancellationPolicy ? (
                <Box
                  sx={{
                    fontSize: '10px',
                    color: isDark ? 'text.secondary' : '#973C00',
                    '& .ql-editor': { padding: 0 },
                  }}
                >
                  <ReactQuill
                    value={restaurant.Experience.CancellationPolicy}
                    readOnly
                    theme={null}
                    modules={{ toolbar: false }}
                  />
                </Box>
              ) : (
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  We understand that circumstances can change. If you need to
                  cancel, please notify us at least 24 hours prior to your
                  scheduled time.
                </Typography>
              )}
            </Box>

            {!loading &&
              restaurant?.Experience?.HouseRules &&
              restaurant?.Experience?.HouseRules !== '<p>﻿</p>' && (
                <Box
                  onClick={handleOpen}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpen();
                    }
                  }}
                  sx={{
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '10px',
                    p: 2,
                    py: 1.25,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      bgcolor: isDark
                        ? 'rgba(255, 255, 255, 0.04)'
                        : 'action.hover',
                    },
                  }}
                >
                  <Checkbox
                    checked={rulesChecked}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpen();
                    }}
                    sx={{
                      p: 0.5,
                      mt: -0.25,
                      color: isDark ? 'text.secondary' : undefined,
                      '&.Mui-checked': { color: 'primary.main' },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: 'text.primary',
                      lineHeight: 1.5,
                      pt: 0.5,
                    }}
                  >
                    Click to view and accept the house rules terms.
                  </Typography>
                </Box>
              )}

            {pointValue &&
              point_id &&
              pointValue !== 'null' &&
              point_id !== 'null' && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: `${currentColor.primary}0d`,
                    borderRadius: '12px',
                    textAlign: 'center',
                  }}
                >
                  <Lottie options={lottieOptions} width={100} height={70} />
                  <Typography sx={{ fontSize: '12px', color: 'text.primary' }}>
                    Earn <strong>{pointValue}</strong> points on completion!
                  </Typography>
                </Box>
              )}

            {isWaitlist ? (
              <Button
                variant="contained"
                fullWidth
                disabled={isConfirmDisabled || confirmLoading}
                onClick={onConfirm}
                sx={{
                  borderRadius: '50px',
                  py: 2,
                  fontWeight: 700,
                  fontSize: '14px',
                  mb: 1.5,
                }}
              >
                {confirmLabel}
              </Button>
            ) : hasDeposit ? (
              <>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    mb: 2,
                    bgcolor: isDark ? 'background.paper' : 'transparent',
                  }}
                >
                  <Typography sx={{ fontWeight: 400, color: 'text.secondary' }}>
                    Payment Method
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Pay Online
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={isConfirmDisabled || confirmLoading}
                  onClick={onConfirm}
                  sx={{
                    borderRadius: '10px',
                    py: 2,
                    fontWeight: 700,
                    fontSize: '14px',
                    mb: 1.5,
                  }}
                >
                  {confirmLabel}
                </Button>
              </>
            ) : (
              <Button
                startIcon={<IoIosCheckmarkCircleOutline />}
                variant="contained"
                fullWidth
                disabled={isConfirmDisabled || confirmLoading}
                onClick={onConfirm}
                sx={{
                  borderRadius: '50px',
                  py: 2,
                  fontWeight: 700,
                  fontSize: '14px',
                  mb: 1.5,
                }}
              >
                {confirmLabel}
              </Button>
            )}

            <Button
              variant="outlined"
              fullWidth
              onClick={onEditDetails}
              startIcon={<FiEdit3 size={16} />}
              sx={{
                borderRadius: '10px',
                py: 1.4,
                fontWeight: 800,
                fontSize: '14px',
                color: 'text.primary',
                borderColor: 'divider',
              }}
            >
              Edit Details
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default ReservationConfirmPanel;
